import React, { useMemo } from "react";
import { StyleSheet } from "react-native";
import { DataTable, Surface, Text } from "react-native-paper";
import { WorkDataItem, formatEmployeeName } from "./types";
import { endOfDay, startOfDay } from "date-fns";

interface PartsTableProps {
  data: WorkDataItem[];
  weekDates: Date[];
  employees: { fullName: string; phone: string; email: string; id: string }[];
}

export const PartsTable: React.FC<PartsTableProps> = ({
  data,
  weekDates,
  employees,
}) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("ru-RU");
  };

  // Подсчет итогов по сотрудникам
  const employeeTotals = useMemo(() => {
    return employees.reduce(
      (acc, employee) => {
        acc[employee.fullName] = data
          .filter((item) => item.employeeName === employee.fullName)
          .reduce((sum, item) => sum + item.amount, 0);
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [data, employees]);

  // Подсчет итогов по датам
  const dateTotals = useMemo(() => {
    return weekDates.reduce(
      (acc, date) => {
        const dateStr = formatDate(date);
        acc[dateStr] = data
          .filter((item) => item.date === dateStr)
          .reduce((sum, item) => sum + item.amount, 0);
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [data, weekDates]);

  return (
    <Surface style={styles.container} elevation={1}>
      <Text variant="titleMedium" style={styles.tableTitle}>
        Запчасти
      </Text>
      <DataTable>
        <DataTable.Header>
          <DataTable.Title style={{ flex: 0.4 }}>Дата</DataTable.Title>
          {employees.map((employee) => (
            <DataTable.Title key={employee.id} numeric>
              {formatEmployeeName(employee.fullName)}
            </DataTable.Title>
          ))}
          <DataTable.Title numeric>Итого по дате</DataTable.Title>
        </DataTable.Header>

        {weekDates.map((date) => {
          const dateStr = formatDate(date);
          return (
            <DataTable.Row key={dateStr}>
              <DataTable.Cell style={{ flex: 0.4 }}>{dateStr}</DataTable.Cell>
              {employees.map((employee) => {
                const dayTotal = data
                  .filter(
                    (item) =>
                      item.date === dateStr &&
                      item.employeeName === employee.fullName,
                  )
                  .reduce((sum, item) => sum + item.amount, 0);

                return (
                  <DataTable.Cell key={`${dateStr}-${employee.id}`} numeric>
                    {dayTotal > 0 ? dayTotal.toLocaleString("ru-RU") : "-"}
                  </DataTable.Cell>
                );
              })}
              <DataTable.Cell numeric>
                {(dateTotals[dateStr] || 0).toLocaleString("ru-RU")}
              </DataTable.Cell>
            </DataTable.Row>
          );
        })}

        <DataTable.Row>
          <DataTable.Cell style={{ flex: 0.4 }}>Итог</DataTable.Cell>
          {employees.map((employee) => (
            <DataTable.Cell key={`total-${employee.id}`} numeric>
              {(employeeTotals[employee.fullName] || 0).toLocaleString("ru-RU")}
            </DataTable.Cell>
          ))}
          <DataTable.Cell numeric>
            {Object.values(employeeTotals)
              .reduce((sum, total) => sum + (total || 0), 0)
              .toLocaleString("ru-RU")}
          </DataTable.Cell>
        </DataTable.Row>
      </DataTable>
    </Surface>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 24,
    borderRadius: 8,
  },
  tableTitle: {
    padding: 16,
    paddingBottom: 8,
  },
});
